/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import { getKeyName, keyNames } from './keyboardUtils';
import cx from './classnames';

export const IE11 = !!window.MSInputMethodContext && !!document.documentMode;

export function getNearestPositionedParent(el) {
	let curEl = el.parentElement;

	while (curEl) {
		if (window.getComputedStyle(curEl).position !== 'static') {
			return curEl;
		}

		curEl = curEl.parentElement;
	}

	return document.body;
}

/**
 * Get the nearest parent element which has a CSS transform applied. If no such parent exists returns null.
 */
export function getTransformedParentElement(node) {
	let el = node;
	do {
		if (
			el.nodeType === Node.ELEMENT_NODE &&
			(el.style.transform || window.getComputedStyle(el).transform !== 'none')
		) {
			return el;
		}
		el = el.parentNode;
	} while (el);

	return null;
}

/**
 * Get a list of all parent nodes.
 */
export function getPathToRoot(node) {
	const path = [];
	let p = node;
	while (p) {
		path.push(p);
		p = p.parentNode;
	}

	return path;
}

function isScrollableOverFlow(overflow) {
	return overflow === 'scroll' || overflow === 'auto';
}

function isScrollableNode(node) {
	const { overflowX, overflowY, overflow } = window.getComputedStyle(node);

	return (
		isScrollableOverFlow(overflowX) ||
		isScrollableOverFlow(overflowY) ||
		isScrollableOverFlow(overflow)
	);
}
// an array of scroll locks
const scrollLocks = [];

/**
 * Determine whether or not the list of nodes contains a scrollable element iff the list also contains the current scroll lock.
 */
function hasScrollableElement(pathNodes) {
	const lockIndex = pathNodes.indexOf(scrollLocks[scrollLocks.length - 1]);
	for (let i = 0; i <= lockIndex; ++i) {
		if (pathNodes[i].nodeType === Node.ELEMENT_NODE) {
			const node = pathNodes[i];

			if (isScrollableNode(node)) {
				return true;
			}
		}
	}

	return false;
}

/**
 * An event listener that prevents scrolling unless the event target is a child of the current scroll lock.
 */
function scrollLockHandler(e) {
	if (e.cancelable) {
		const { tagName } = e.target;

		if (
			tagName !== 'INPUT' &&
			tagName !== 'TEXTAREA' &&
			!hasScrollableElement(getPathToRoot(e.target))
		) {
			e.preventDefault();
		}
	}
}

function scrollLockHandlerKeyDown(e) {
	if (e.keyCode >= 37 && e.keyCode <= 40) {
		scrollLockHandler(e);
	}
}

/**
 * Lock scrolling on the page to the specified element.
 * Prevents scrolling of all elements on the page other than those that are descendent of the given element.
 */
export function scrollLock(element) {
	if (scrollLocks.indexOf(element) >= 0) {
		return;
	}

	if (scrollLocks.length === 0) {
		document.addEventListener('keydown', scrollLockHandlerKeyDown, true);
		document.addEventListener('wheel', scrollLockHandler, true);
		document.addEventListener('mousewheel', scrollLockHandler, true);
		document.addEventListener('touchstart', scrollLockHandler, true);
		document.addEventListener('touchmove', scrollLockHandler, true);
		// IE
		document.addEventListener('DOMMouseScroll', scrollLockHandler, true);
	}

	scrollLocks.push(element);
}

/**
 * Unlocks scrolling which was previously locked by the given element.
 */
export function scrollUnlock(element) {
	const index = scrollLocks.indexOf(element);
	if (index === -1) {
		return;
	}

	scrollLocks.splice(index, 1);

	if (scrollLocks.length === 0) {
		document.removeEventListener('keydown', scrollLockHandlerKeyDown, true);
		document.removeEventListener('wheel', scrollLockHandler, true);
		document.removeEventListener('mousewheel', scrollLockHandler, true);
		document.removeEventListener('touchstart', scrollLockHandler, true);
		document.removeEventListener('touchmove', scrollLockHandler, true);
		// IE
		document.removeEventListener('DOMMouseScroll', scrollLockHandler, true);
	}
}

/**
 * Gets an x,y offset from a mouse event
 */
export function getEventClientOffset(e) {
	let clientX;
	let clientY;

	// get the underlying event in the case of jquery
	const event = e.originalEvent || e;
	const touches = event.type === 'touchend' ? event.changedTouches : event.touches;

	if (touches && touches.length) {
		[{ clientX, clientY }] = touches;
	} else {
		clientX = e.clientX;
		clientY = e.clientY;
	}

	return {
		x: clientX,
		y: clientY,
	};
}

/**
 * Gets an x,y offset from nearest element
 */
export function getNodeClientOffset(node) {
	const el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;

	if (!el) {
		return null;
	}

	const { top, left } = el.getBoundingClientRect();

	return { x: left, y: top };
}

/**
 * Gets the depth of a node within its tree
 */
export function getNodeDepth(node) {
	let depth = 0;
	let n = node;

	while (n) {
		depth += 1;
		n = n.parentNode;
	}

	return depth;
}

/**
 * Get the center of the bounding rect of the element
 */
export function getElementCenter(el) {
	const rect = el.getBoundingClientRect();
	const x = Math.round(rect.left + rect.width / 2);
	const y = Math.round(rect.top + rect.height / 2);

	return { x, y };
}

/**
 * Get the top left of the bounding rect of the element
 */
export function getElementTopLeft(el) {
	const rect = el.getBoundingClientRect();
	const x = Math.round(rect.left);
	const y = Math.round(rect.top);

	return { x, y };
}

/**
 * Get the top left of the bounding rect of the element
 */
export function getElementBottomLeft(el) {
	const rect = el.getBoundingClientRect();
	const x = Math.round(rect.left);
	const y = Math.round(rect.bottom);

	return { x, y };
}

export function offsetWithinBoundingRect(clientOffset, boundingRect) {
	return (
		clientOffset.x >= boundingRect.left &&
		clientOffset.x < boundingRect.right &&
		clientOffset.y >= boundingRect.top &&
		clientOffset.y < boundingRect.bottom
	);
}

export function applyAttributes(el, attrs) {
	const attrKeys = Object.keys(attrs);

	for (let i = 0; i < attrKeys.length; i++) {
		const attrKey = attrKeys[i];

		if (attrKey === 'style') {
			const style = attrs[attrKey];
			if (!style || typeof style !== 'object') {
				throw new Error('applyAttributes: Expected style to be an object');
			}

			const styleNames = Object.keys(style);

			for (let j = 0; j < styleNames.length; j++) {
				const styleName = styleNames[j];
				el.style[styleName] = style[styleName];
			}
		} else if (attrKey === 'className') {
			const className = attrs[attrKey];
			if (typeof className === 'string') {
				const classes = className.split(' ');
				for (let j = 0; j < classes.length; j++) {
					el.classList.add(classes[j]);
				}
			} else {
				// object
				Object.keys(className).forEach(cn => {
					if (className[cn]) {
						el.classList.add(cn);
					} else {
						el.classList.remove(cn);
					}
				});
			}
		} else if (attrKey === 'class') {
			throw new Error(
				'applyAttributes: Unexpected attribute "class", use "className" instead.'
			);
		} else {
			el.setAttribute(attrKey, attrs[attrKey]);
		}
	}
}

/**
 * Vertical cross browser support for experimental DOM API #scrollIntoViewIfNeeded
 * This function will only scroll vertically.
 */
export function scrollIntoViewIfNeeded(scrollNode, node) {
	if (node.scrollIntoViewIfNeeded) {
		node.scrollIntoViewIfNeeded();
	} else {
		const sOffsetTop = scrollNode.offsetTop;
		const sScrollTop = scrollNode.scrollTop;
		const sClientHeight = scrollNode.clientHeight;

		const nOffsetTop = node.offsetTop;
		const nClientHeight = node.clientHeight;

		const nTop = nOffsetTop - sOffsetTop;

		const overTop = nTop < sScrollTop;

		if (overTop && nTop + nClientHeight > sScrollTop) {
			// move to top
			scrollNode.scrollTop = nTop;
		}

		const overBottom = nTop + nClientHeight > sScrollTop + sClientHeight;

		if (overBottom && nTop < sScrollTop + sClientHeight) {
			// move to bottom
			scrollNode.scrollTop =
				sScrollTop + (nTop + nClientHeight - (sScrollTop + sClientHeight));
		} else if (overTop || overBottom) {
			// center
			scrollNode.scrollTop = nTop - sClientHeight / 2 + nClientHeight / 2;
		}
	}
}

export function isElementInView(el, parent = document.documentElement) {
	const rect = el.getBoundingClientRect();
	const parentRect = parent.getBoundingClientRect();

	return (
		rect.top >= parentRect.top &&
		rect.left >= parentRect.left &&
		rect.bottom <= parentRect.bottom &&
		rect.right <= parentRect.right
	);
}

/**
 * Creates two headers so that only the elements between the two headers are tabable
 * Argument: tabEnabledArea: DOM element
 */

export function enableFocusLoop(tabEnabledArea, index, ariaLabel) {
	const focusIndex = index || 0;
	let tabKeyPressed = false;

	const entryHeader = document.createElement('div');
	entryHeader.setAttribute('tabindex', '0');
	entryHeader.setAttribute('style', 'width: 0; height: 0; outline: none');
	if (ariaLabel) {
		entryHeader.setAttribute('aria-label', ariaLabel);
	}

	const exitHeader = document.createElement('div');
	exitHeader.setAttribute('tabindex', '0');
	exitHeader.setAttribute('style', 'width: 0; height: 0; outline: none');

	tabEnabledArea.insertBefore(entryHeader, tabEnabledArea.childNodes[0]);
	tabEnabledArea.appendChild(exitHeader);

	tabEnabledArea.addEventListener('keydown', e => {
		getKeyName(e) == keyNames.TAB &&
			((tabKeyPressed = true), e.target.classList.remove(cx('focus')));
	});

	const nodesArray = getFocusableElements(tabEnabledArea);
	const { activeElement } = document;

	(focusIndex < 0 || focusIndex > nodesArray.length - 2) &&
		!tabEnabledArea.contains(activeElement) &&
		setTimeout(() => entryHeader.focus(), 0);

	setTimeout(() => {
		tabEnabledArea.contains(activeElement) && activeElement.focus();
		entryHeader.addEventListener(
			'focus',
			e => {
				e.preventDefault();
				const nodesArray = getFocusableElements(tabEnabledArea);
				if (tabKeyPressed) {
					nodesArray[nodesArray.length - 2].focus();
					tabKeyPressed = false;
				}
			},
			0
		);

		exitHeader.addEventListener('focus', e => {
			e.preventDefault();
			const nodesArray = getFocusableElements(tabEnabledArea);
			if (tabKeyPressed) {
				nodesArray[1].focus();
				tabKeyPressed = false;
			}
		});
	});

	focusIndex > -1 &&
		focusIndex < nodesArray.length - 2 &&
		!tabEnabledArea.contains(activeElement) &&
		setFocus(nodesArray[focusIndex + 1]);
}

export function resetFocusLoop(tabEnabledArea) {
	const nodesArray = getFocusableElements(tabEnabledArea);
	if (!tabEnabledArea.contains(document.activeElement)) {
		nodesArray[0].focus();
	}
}

export function getFocusableElements(tabEnabledArea) {
	const focusableElements = tabEnabledArea.querySelectorAll(
		'button:not([tabindex="-1"]):not([disabled]), [href]:not([tabindex="-1"]):not([disabled]), input:not([tabindex="-1"]):not([disabled]), select:not([tabindex="-1"]):not([disabled]), textarea:not([tabindex="-1"]):not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
	);
	// filter out all svg items or elements with undefined 'focus' function
	return fromNodelistToArray(focusableElements).filter(
		item =>
			!(
				item instanceof SVGElement ||
				typeof item.focus !== 'function' ||
				(item.type == 'radio' && !item.checked)
			)
	);
}

/**
 * Calculates the available space for a flyout container and returns an object containing x and y locations
 * Argument: trigger: DOM element that triggers the flyout container,
 *           target: DOM element, the flyout container,
 *           defaultPosition (Optional): Object contains default x and y locations
 */

export function availableSpace(element) {
	const elementRect = element.getBoundingClientRect();
	const availableRoomLeft = elementRect.left;
	const availableRoomRight = window.innerWidth - elementRect.right;
	const availableRoomTop = elementRect.top;
	const availableRoomBottom = window.innerHeight - elementRect.bottom;

	return {
		top: availableRoomTop,
		right: availableRoomRight,
		bottom: availableRoomBottom,
		left: availableRoomLeft,
	};
}

export function autoPosition(availableSpace, targetDimension, defaultPosition) {
	const targetHeight = targetDimension.height;
	const targetWidth = targetDimension.width;

	const possibleSpots = [];

	if (
		availableSpace.top >= targetHeight &&
		availableSpace.right >= targetWidth / 2 &&
		availableSpace.left >= targetWidth / 2
	) {
		possibleSpots.push('top');
	}

	if (
		availableSpace.bottom >= targetHeight &&
		availableSpace.right >= targetWidth / 2 &&
		availableSpace.left >= targetWidth / 2
	) {
		possibleSpots.push('bottom');
	}

	if (
		availableSpace.right >= targetWidth &&
		availableSpace.top >= targetHeight / 2 &&
		availableSpace.bottom >= targetHeight / 2
	) {
		possibleSpots.push('right');
	}

	if (
		availableSpace.left >= targetWidth &&
		availableSpace.top >= targetHeight / 2 &&
		availableSpace.bottom >= targetHeight / 2
	) {
		possibleSpots.push('left');
	}

	if (defaultPosition && possibleSpots.indexOf(defaultPosition) > -1) {
		return defaultPosition;
	} else if (possibleSpots.length > 0) {
		return possibleSpots[0];
	}

	return 'top';
}

export function fromNodelistToArray(nodelist) {
	return Array.prototype.slice.apply(nodelist);
}

export function setFocus(focusableElement) {
	focusableElement.focus();
	focusableElement.classList.add(cx('focus'));
}

export function disableFocusLoop(tabEnabledArea) {
	const focusableElements = tabEnabledArea.querySelectorAll('[tabindex]:not([tabindex="-1"])');
	const nodesArray = fromNodelistToArray(focusableElements);
	nodesArray.forEach((item, index) => {
		item.removeEventListener('focus', setFocus);
	});
	tabEnabledArea.removeEventListener('click', setFocus);
}

/**
 * Computes the position of an element - used for tooltip triggers
 * Argument: elem: DOM element that triggers the tooltip
 */

export function computeTopWith(elem) {
	const box = elem.getBoundingClientRect();

	const { body } = document;
	const element = elem;

	const scrollTop = window.pageYOffset || element.scrollTop || body.scrollTop;

	const clientTop = element.clientTop || body.clientTop || 0;

	const top = box.top + scrollTop - clientTop;

	return Math.round(top);
}

export function computeLeftWith(elem) {
	const box = elem.getBoundingClientRect();

	const { body } = document;
	const element = elem;

	const scrollLeft = window.pageXOffset || element.scrollLeft || body.scrollLeft;

	const clientLeft = element.clientLeft || body.clientLeft || 0;

	const left = box.left + scrollLeft - clientLeft;

	return Math.round(left);
}

export function computeWidthOf(elem) {
	return Math.round(elem.getBoundingClientRect().width);
}
